/**
 * Système de tracking des crédits consommés (créations et actions IA)
 *
 * Les crédits consommés ne se remboursent jamais (sauf reset mensuel pour auth users)
 * - 1 conversation créée = 1 crédit consommé
 * - 1 poll créé = 1 crédit consommé
 * - 1 message IA = 1 crédit consommé
 * - 1 query analytics IA = 1 crédit consommé
 * - 1 simulation complète = 5 crédits consommés
 *
 * Même si l'utilisateur supprime ses conversations/polls, les crédits restent consommés.
 */

import { getCurrentUserId } from "./guestIdentityService";
import { logger } from "./logger";
import { consumeGuestCredits } from "./guestQuotaService";
import { ErrorFactory, logError } from "./error-handling";
import { isE2ETestingEnvironment } from "./e2e-detection";
import { invokeSupabaseFunction, supabaseSelectSingle } from "./supabaseApi";

const STORAGE_KEY = "doodates_quota_consumed";
const JOURNAL_KEY = "doodates_quota_journal";

// Cache pour éviter les appels répétés à l'Edge Function
const quotaCache = new Map<
  string,
  { data: QuotaConsumedData; timestamp: number }
>();
const QUOTA_CACHE_TTL = 5000; // 5 secondes

// Dédup in-flight : si plusieurs hooks (useQuota, useConversations,
// useSimulation, useAiMessageQuota) montés en parallèle déclenchent
// simultanément un fetch, on partage la même promesse au lieu de spammer
// l'Edge Function (3-5 calls parallèles → 1 seul). Le cache TTL ne couvre
// pas ce cas : tous les callers voient cache vide avant que le 1er finisse.
const inflightQuotaFetch = new Map<string, Promise<QuotaConsumedData | null>>();
const inflightJournalFetch = new Map<string, Promise<CreditJournalEntry[]>>();

export type CreditActionType =
  | "conversation_created"
  | "poll_created"
  | "ai_message"
  | "analytics_query"
  | "simulation"
  | "other";

export interface CreditJournalEntry {
  id: string;
  timestamp: string;
  action: CreditActionType;
  credits: number;
  userId: string;
  metadata?: {
    conversationId?: string;
    pollId?: string;
    pollType?: "date" | "form" | "quizz" | "availability"; // Obligatoire pour action = "poll_created"
    pollTypeLabel?: string;
    simulationId?: string;
    analyticsQuery?: string;
    [key: string]: unknown;
  };
}

interface QuotaConsumedData {
  conversationsCreated: number;
  // Compteurs séparés par type de poll (pollsCreated supprimé - calculer à la volée)
  datePollsCreated: number;
  formPollsCreated: number;
  quizzCreated: number;
  availabilityPollsCreated: number;
  aiMessages: number;
  analyticsQueries: number;
  simulations: number;
  totalCreditsConsumed: number; // Total de tous les crédits
  subscriptionStartDate?: string; // Date de début d'abonnement (pour reset mensuel)
  lastResetDate?: string; // Date du dernier reset
  userId: string; // "guest" ou user.id
}

/**
 * Calcule le total de polls créés à partir des compteurs séparés
 * (remplace l'ancien pollsCreated qui était maintenu par trigger SQL)
 */
export function calculateTotalPollsCreated(quota: {
  datePollsCreated: number;
  formPollsCreated: number;
  quizzCreated: number;
  availabilityPollsCreated: number;
}): number {
  return (
    quota.datePollsCreated +
    quota.formPollsCreated +
    quota.quizzCreated +
    quota.availabilityPollsCreated
  );
}

interface AllQuotaData {
  [userId: string]: QuotaConsumedData;
}

/**
 * Obtenir la date de début d'abonnement depuis le profil utilisateur
 */
// Cache pour les dates d'abonnement (évite les appels Supabase répétés)
const subscriptionDateCache = new Map<
  string,
  { date: Date | null; timestamp: number }
>();
const SUBSCRIPTION_CACHE_TTL = 5 * 60 * 1000; // 5 minutes

async function getSubscriptionStartDate(
  userId: string | null | undefined,
): Promise<Date | null> {
  if (!userId || userId === "guest") return null;

  // Vérifier le cache d'abord
  const cached = subscriptionDateCache.get(userId);
  if (cached && Date.now() - cached.timestamp < SUBSCRIPTION_CACHE_TTL) {
    return cached.date;
  }

  try {
    // Timeout de 2 secondes pour éviter de bloquer
    try {
      const profile = await Promise.race([
        supabaseSelectSingle<{
          subscription_expires_at: string | null;
          created_at: string;
        }>(
          "profiles",
          {
            id: `eq.${userId}`,
            select: "subscription_expires_at,created_at",
          },
          { timeout: 2000 },
        ),
        new Promise<never>((_, reject) => {
          setTimeout(() => reject(new Error("Timeout")), 2000);
        }),
      ]);

      if (profile?.subscription_expires_at) {
        const result = new Date(profile.subscription_expires_at);
        subscriptionDateCache.set(userId, {
          date: result,
          timestamp: Date.now(),
        });
        return result;
      }
      if (profile?.created_at) {
        const result = new Date(profile.created_at);
        subscriptionDateCache.set(userId, {
          date: result,
          timestamp: Date.now(),
        });
        return result;
      }
      subscriptionDateCache.set(userId, { date: null, timestamp: Date.now() });
      return null;
    } catch (error: unknown) {
      if (error instanceof Error && error.message.includes("Timeout")) {
        subscriptionDateCache.set(userId, {
          date: null,
          timestamp: Date.now(),
        });
        return null;
      }
      throw error;
    }
  } catch (error) {
    logger.debug("Impossible de récupérer la date d'abonnement", "quota", {
      error,
    });
    // Mettre null en cache pour éviter les appels répétés en cas d'erreur
    subscriptionDateCache.set(userId, { date: null, timestamp: Date.now() });
    return null;
  }
}

/**
 * Calculer la prochaine date de reset basée sur la date d'abonnement
 */
function calculateNextResetDate(subscriptionStartDate: Date | null): Date {
  if (!subscriptionStartDate) {
    // Par défaut, reset le 1er du mois suivant
    const nextReset = new Date();
    nextReset.setMonth(nextReset.getMonth() + 1);
    nextReset.setDate(1);
    return nextReset;
  }

  // Calculer le reset mensuel basé sur la date d'abonnement
  const now = new Date();
  const subscriptionDay = subscriptionStartDate.getDate();
  const subscriptionMonth = subscriptionStartDate.getMonth();
  const subscriptionYear = subscriptionStartDate.getFullYear();

  // Prochaine date de reset = même jour du mois suivant
  let nextReset = new Date(
    subscriptionYear,
    subscriptionMonth,
    subscriptionDay,
  );

  // Si on est déjà passé ce mois, prendre le mois suivant
  if (nextReset <= now) {
    nextReset = new Date(
      subscriptionYear,
      subscriptionMonth + 1,
      subscriptionDay,
    );
  }

  return nextReset;
}

/**
 * Appeler l'Edge Function pour récupérer le quota depuis Supabase
 */
async function fetchQuotaFromServer(
  userId: string,
): Promise<QuotaConsumedData | null> {
  // Dédup : partager la promesse en cours pour ce userId.
  const existing = inflightQuotaFetch.get(userId);
  if (existing) return existing;

  const promise = (async (): Promise<QuotaConsumedData | null> => {
    try {
      const data = await invokeSupabaseFunction<{
        success?: boolean;
        currentQuota?: Record<string, unknown>;
      }>(
        "quota-tracking",
        { endpoint: "checkQuota", action: "other", credits: 0 },
        { timeout: 2000 },
      );

      if (!data.success || !data.currentQuota) {
        return null;
      }
      const q = data.currentQuota as Record<string, number | string>;
      return {
        conversationsCreated: (q.conversationsCreated as number) || 0,
        datePollsCreated: (q.datePollsCreated as number) || 0,
        formPollsCreated: (q.formPollsCreated as number) || 0,
        quizzCreated: (q.quizzCreated as number) || 0,
        availabilityPollsCreated: (q.availabilityPollsCreated as number) || 0,
        aiMessages: (q.aiMessages as number) || 0,
        analyticsQueries: (q.analyticsQueries as number) || 0,
        simulations: (q.simulations as number) || 0,
        totalCreditsConsumed: (q.totalCreditsConsumed as number) || 0,
        subscriptionStartDate: q.subscriptionStartDate as string | undefined,
        lastResetDate: q.lastResetDate as string | undefined,
        userId,
      };
    } catch (error) {
      logger.debug("Erreur récupération quota serveur", "quota", { error });
      return null;
    }
  })();

  inflightQuotaFetch.set(userId, promise);
  try {
    return await promise;
  } finally {
    inflightQuotaFetch.delete(userId);
  }
}

/**
 * Obtenir les données de quota consommé pour l'utilisateur actuel
 */
export async function getQuotaConsumed(
  userId: string | null | undefined,
): Promise<QuotaConsumedData> {
  try {
    const currentUserId = userId || getCurrentUserId();
    const isGuest = !userId || userId === "guest";
    const isE2E =
      isE2ETestingEnvironment() ||
      (typeof window !== "undefined" &&
        ((window as Window & { __IS_E2E_TESTING__?: boolean })
          .__IS_E2E_TESTING__ === true ||
          localStorage.getItem("e2e") === "1" ||
          localStorage.getItem("dev-local-mode") === "1"));

    // Pour les utilisateurs authentifiés, essayer de récupérer depuis le serveur
    if (!isGuest && !isE2E && userId) {
      // Vérifier le cache d'abord
      const cached = quotaCache.get(userId);
      if (cached && Date.now() - cached.timestamp < QUOTA_CACHE_TTL) {
        return cached.data;
      }

      // Migrer automatiquement localStorage → Supabase si nécessaire (une seule fois)
      const migrationKey = `quota_migrated_${userId}`;
      if (!localStorage.getItem(migrationKey)) {
        try {
          // Migration inline pour éviter problème de chemin
          const STORAGE_KEY_MIG = "doodates_quota_consumed";
          const JOURNAL_KEY_MIG = "doodates_quota_journal";
          const storageDataMig = localStorage.getItem(STORAGE_KEY_MIG);
          const journalDataMig = localStorage.getItem(JOURNAL_KEY_MIG);

          if (storageDataMig) {
            const allDataMig: AllQuotaData = JSON.parse(storageDataMig);
            const userDataMig = allDataMig[userId];

            if (userDataMig) {
              // Créer le quota via Edge Function (consumeCredits avec 0 crédits)
              await invokeSupabaseFunction(
                "quota-tracking",
                {
                  endpoint: "consumeCredits",
                  action: "other",
                  credits: 0,
                  metadata: { migrated: true },
                },
                { timeout: 5000 },
              ).catch(() => {
                // Ignorer les erreurs de migration
              });

              // Marquer comme migré (idempotent côté serveur)
              localStorage.setItem(migrationKey, "true");
            }
          }
        } catch (error) {
          logger.debug("Erreur migration automatique", "quota", { error });
          // Continuer même si migration échoue
        }
      }

      const serverQuota = await fetchQuotaFromServer(userId);
      if (serverQuota) {
        quotaCache.set(userId, { data: serverQuota, timestamp: Date.now() });
        return serverQuota;
      }
      // Si serveur indisponible, fallback localStorage
    }

    // Fallback localStorage (pour guests, E2E, ou si serveur indisponible)
    const storageData = localStorage.getItem(STORAGE_KEY);

    if (!storageData) {
      const subscriptionStart = await getSubscriptionStartDate(userId);
      return {
        conversationsCreated: 0,
        datePollsCreated: 0,
        formPollsCreated: 0,
        quizzCreated: 0,
        availabilityPollsCreated: 0,
        aiMessages: 0,
        analyticsQueries: 0,
        simulations: 0,
        totalCreditsConsumed: 0,
        subscriptionStartDate: subscriptionStart?.toISOString(),
        userId: currentUserId,
      };
    }

    const allData: AllQuotaData = JSON.parse(storageData);
    let userData = allData[currentUserId];

    if (!userData) {
      const subscriptionStart = await getSubscriptionStartDate(userId);
      userData = {
        conversationsCreated: 0,
        datePollsCreated: 0,
        formPollsCreated: 0,
        quizzCreated: 0,
        availabilityPollsCreated: 0,
        aiMessages: 0,
        analyticsQueries: 0,
        simulations: 0,
        totalCreditsConsumed: 0,
        subscriptionStartDate: subscriptionStart?.toISOString(),
        userId: currentUserId,
      };
    } else {
      // Migration automatique : ajouter les nouveaux compteurs s'ils n'existent pas
      if (userData.datePollsCreated === undefined)
        userData.datePollsCreated = 0;
      if (userData.formPollsCreated === undefined)
        userData.formPollsCreated = 0;
      if (userData.quizzCreated === undefined) userData.quizzCreated = 0;
      if (userData.availabilityPollsCreated === undefined)
        userData.availabilityPollsCreated = 0;
    }

    // Vérifier reset mensuel pour auth users basé sur la date d'abonnement
    if (userId && userId !== "guest") {
      const subscriptionStart = userData.subscriptionStartDate
        ? new Date(userData.subscriptionStartDate)
        : await getSubscriptionStartDate(userId);

      if (subscriptionStart) {
        const nextReset = calculateNextResetDate(subscriptionStart);
        const now = new Date();

        // Si on a dépassé la date de reset, réinitialiser
        if (!userData.lastResetDate || new Date(userData.lastResetDate) < now) {
          if (now >= nextReset) {
            const resetData: QuotaConsumedData = {
              conversationsCreated: 0,
              datePollsCreated: 0,
              formPollsCreated: 0,
              quizzCreated: 0,
              availabilityPollsCreated: 0,
              aiMessages: 0,
              analyticsQueries: 0,
              simulations: 0,
              totalCreditsConsumed: 0,
              subscriptionStartDate: subscriptionStart.toISOString(),
              lastResetDate:
                calculateNextResetDate(subscriptionStart).toISOString(),
              userId: userId,
            };

            // Sauvegarder le reset
            allData[userId] = resetData;
            localStorage.setItem(STORAGE_KEY, JSON.stringify(allData));

            return resetData;
          }
        }
      }
    }

    return userData;
  } catch (error) {
    logger.error("Erreur lors de la lecture du quota consommé", error);
    return {
      conversationsCreated: 0,
      datePollsCreated: 0,
      formPollsCreated: 0,
      quizzCreated: 0,
      availabilityPollsCreated: 0,
      aiMessages: 0,
      analyticsQueries: 0,
      simulations: 0,
      totalCreditsConsumed: 0,
      userId: userId || "guest",
    };
  }
}

/**
 * Helper UI: obtenir uniquement le total de crédits consommés pour un utilisateur
 * Réutilise getQuotaConsumed pour respecter toute la logique existante (Edge Function, fallback, reset)
 */
export async function getTotalCreditsConsumed(
  userId: string | null | undefined,
): Promise<number> {
  const quota = await getQuotaConsumed(userId);
  return quota.totalCreditsConsumed;
}

/**
 * Ajouter une entrée au journal de consommation
 */
function addJournalEntry(
  entry: Omit<CreditJournalEntry, "id" | "timestamp">,
): void {
  try {
    const journalData = localStorage.getItem(JOURNAL_KEY);
    const journal: CreditJournalEntry[] = journalData
      ? JSON.parse(journalData)
      : [];

    const newEntry: CreditJournalEntry = {
      id: `entry_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date().toISOString(),
      ...entry,
    };

    journal.push(newEntry);

    // Garder seulement les 1000 dernières entrées pour éviter l'explosion du localStorage
    if (journal.length > 1000) {
      journal.splice(0, journal.length - 1000);
    }

    localStorage.setItem(JOURNAL_KEY, JSON.stringify(journal));

    logger.debug("Entrée ajoutée au journal de consommation", "quota", {
      action: entry.action,
      credits: entry.credits,
      userId: entry.userId,
    });
  } catch (error) {
    logger.error("Erreur lors de l'ajout au journal", error);
  }
}

/**
 * Obtenir le journal de consommation pour un utilisateur
 */
export async function getConsumptionJournal(
  userId: string | null | undefined,
  limit: number = 100,
): Promise<CreditJournalEntry[]> {
  try {
    const currentUserId = userId || getCurrentUserId();
    const isGuest = !userId || userId === "guest";
    const isE2E =
      isE2ETestingEnvironment() ||
      (typeof window !== "undefined" &&
        ((window as Window & { __IS_E2E_TESTING__?: boolean })
          .__IS_E2E_TESTING__ === true ||
          localStorage.getItem("e2e") === "1" ||
          localStorage.getItem("dev-local-mode") === "1"));

    // Pour les utilisateurs authentifiés, essayer de récupérer depuis le serveur
    if (!isGuest && !isE2E && userId) {
      // Dédup in-flight par (userId, limit) — évite N hooks parallèles
      // (ConsumptionJournal, AdminUserActivity…) qui spammeraient l'Edge Function.
      const journalKey = `${userId}:${limit}`;
      const existing = inflightJournalFetch.get(journalKey);
      if (existing) return existing;

      const promise = (async (): Promise<CreditJournalEntry[]> => {
        try {
          const data = await invokeSupabaseFunction<{
            success?: boolean;
            journal?: CreditJournalEntry[];
          }>(
            "quota-tracking",
            { endpoint: "getJournal", limit },
            { timeout: 2000 },
          );
          if (data.success && data.journal && data.journal.length > 0) {
            return data.journal;
          }
        } catch (error) {
          logger.debug("Erreur récupération journal serveur", "quota", {
            error,
          });
          // Fallback localStorage
        }
        return [];
      })();

      inflightJournalFetch.set(journalKey, promise);
      try {
        const result = await promise;
        if (result.length > 0) return result;
      } finally {
        inflightJournalFetch.delete(journalKey);
      }
    }

    // Fallback localStorage
    const journalData = localStorage.getItem(JOURNAL_KEY);
    if (!journalData) return [];

    const journal: CreditJournalEntry[] = JSON.parse(journalData);

    // Filtrer par utilisateur et trier par date (plus récent en premier)
    const userJournal = journal
      .filter((entry) => entry.userId === currentUserId)
      .sort(
        (a, b) =>
          new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
      )
      .slice(0, limit);

    return userJournal;
  } catch (error) {
    logger.error("Erreur lors de la lecture du journal", error);
    return [];
  }
}

/**
 * Fonction générique pour consommer des crédits
 */
async function consumeCredits(
  userId: string | null | undefined,
  credits: number,
  action: CreditActionType,
  metadata?: CreditJournalEntry["metadata"],
): Promise<void> {
  try {
    // Pour les guests, utiliser "guest" comme userId pour le tracking
    // getCurrentUserId() retourne un deviceId, mais on veut "guest" pour le tracking des crédits
    const isGuest = !userId || userId === "guest";
    const currentUserId = userId || "guest";
    const isE2E =
      isE2ETestingEnvironment() ||
      (typeof window !== "undefined" &&
        ((window as Window & { __IS_E2E_TESTING__?: boolean })
          .__IS_E2E_TESTING__ === true ||
          localStorage.getItem("e2e") === "1" ||
          localStorage.getItem("dev-local-mode") === "1"));

    // Mode utilisateur illimité pour les tests E2E :
    // - Permet de vérifier le tracking fin (aiMessages, totalCreditsConsumed, journal)
    // - Sans être bloqué par les limites réelles de quota (popup "Crédits IA épuisés")
    // Ce userId ne doit être utilisé que dans les tests automatisés.
    const isUnlimitedE2ETestUser = isE2E && userId === "quota-test-unlimited";

    // Pour les guests, utiliser le service Supabase sécurisé
    // SAUF en mode E2E (où on utilise localStorage directement)
    // et SAUF pour l'utilisateur spécial d'E2E "quota-test-unlimited" qui ne doit pas être bloqué.
    if (isGuest && !isE2E && !isUnlimitedE2ETestUser) {
      try {
        // Timeout réduit à 2 secondes (les timeouts internes sont déjà à 1s)
        const timeoutPromise = new Promise<{ success: false; error: string }>(
          (resolve) => {
            setTimeout(
              () =>
                resolve({
                  success: false,
                  error: "Timeout: Supabase is slow or unavailable",
                }),
              2000,
            );
          },
        );

        const consumePromise = consumeGuestCredits(action, credits, metadata);
        const result = await Promise.race([consumePromise, timeoutPromise]);

        if (!result.success) {
          // Si c'est un timeout, permettre l'action mais logger un avertissement (seulement une fois)
          if (result.error?.includes("Timeout")) {
            // Ne logger qu'une fois toutes les 10 secondes pour éviter le spam
            const lastLogKey = `quota_timeout_${action}`;
            const lastLog =
              (globalThis as typeof globalThis & { [key: string]: number })[
                lastLogKey
              ] || 0;
            if (Date.now() - lastLog > 10000) {
              logger.warn(
                "Guest credit consumption timeout - allowing action but quota may be inaccurate",
                "quota",
                {
                  action,
                  credits,
                  error: result.error,
                },
              );
              (globalThis as typeof globalThis & { [key: string]: number })[
                lastLogKey
              ] = Date.now();
            }
            // Continuer avec localStorage pour permettre l'action de continuer
          } else {
            // Pour les vraies erreurs de quota (limite atteinte), bloquer l'action
            logger.warn("Guest credit consumption failed", "quota", {
              action,
              credits,
              error: result.error,
            });
            throw ErrorFactory.validation(
              result.error || "Credit limit reached",
              "Limite de crédits atteinte",
            );
          }
        } else {
          // En mode E2E, on continue pour mettre à jour localStorage
          // Sinon, on retourne car consumeGuestCredits a déjà géré la logique serveur
          if (!isE2E) {
            return;
          }
        }
      } catch (error) {
        // Si c'est une erreur de validation (quota atteint), la propager
        if (
          error instanceof Error &&
          (error.message.includes("limit") || error.message.includes("Limite"))
        ) {
          throw error;
        }
        // Pour les autres erreurs (timeout, réseau), continuer silencieusement avec localStorage
        // Ne logger qu'une fois toutes les 10 secondes
        const lastLogKey = `quota_error_${action}`;
        const lastLog =
          (globalThis as typeof globalThis & { [key: string]: number })[
            lastLogKey
          ] || 0;
        if (Date.now() - lastLog > 10000) {
          logger.warn(
            "Guest credit consumption error - allowing action but quota may be inaccurate",
            "quota",
            {
              action,
              credits,
              error: error instanceof Error ? error.message : String(error),
            },
          );
          (globalThis as typeof globalThis & { [key: string]: number })[
            lastLogKey
          ] = Date.now();
        }
        // Continuer avec localStorage pour permettre l'action de continuer
      }
    }

    // Pour les utilisateurs authentifiés, utiliser Edge Function (sauf E2E)
    // et sauf pour l'utilisateur spécial d'E2E "quota-test-unlimited" qui doit rester localStorage-only.
    if (!isGuest && !isE2E && userId && !isUnlimitedE2ETestUser) {
      console.log(
        `[quotaTracking] 🔒 consumeCredits - Mode auth, appel Edge Function...`,
        {
          userId,
          action,
          credits,
        },
      );

      try {
        console.log(
          `[quotaTracking] 🔒 Appel Edge Function pour consommation quota...`,
        );
        const requestBody = {
          endpoint: "consumeCredits",
          action,
          credits,
          metadata: metadata || {},
        };
        console.log(
          `[quotaTracking] 🔒 Request body:`,
          JSON.stringify(requestBody, null, 2),
        );

        const fetchStartTime = Date.now();
        const result = await invokeSupabaseFunction<{
          success: boolean;
          quota?: QuotaConsumedData;
          error?: string;
        }>("quota-tracking", requestBody, { timeout: 2000 });
        const fetchDuration = Date.now() - fetchStartTime;

        console.log(
          `[quotaTracking] 🔒 Réponse Edge Function reçue (${fetchDuration}ms):`,
          {
            success: result.success,
            error: result.error,
            quota: result.quota
              ? {
                  aiMessages: result.quota.aiMessages,
                  totalCreditsConsumed: result.quota.totalCreditsConsumed,
                }
              : null,
          },
        );

        if (result.success) {
          console.log(`[quotaTracking] ✅ Crédits consommés via serveur`);
          // Invalider le cache
          quotaCache.delete(userId);
          logger.debug("Crédits consommés via serveur", "quota", {
            action,
            credits,
            total: result.quota?.totalCreditsConsumed,
            userId,
          });
          return; // Succès, on sort
        } else {
          // Erreur, fallback localStorage
          console.log(
            `[quotaTracking] ⚠️ Edge Function erreur, fallback localStorage`,
          );
          logger.warn("Edge Function erreur, fallback localStorage", "quota", {
            error: result.error,
          });
        }
      } catch (error) {
        logError(
          ErrorFactory.storage(
            "Erreur consommation serveur",
            "Une erreur est survenue lors de la consommation des crédits",
          ),
          {
            metadata: {
              error,
              action,
              credits,
              userId,
            },
          },
        );
        // Si c'est une erreur de validation (quota atteint), la propager
        if (
          error instanceof Error &&
          (error.message.includes("limit") ||
            error.message.includes("Limite") ||
            error.message.includes("reached"))
        ) {
          throw error;
        }
        // Pour les autres erreurs (timeout, réseau), fallback localStorage
        logger.warn(
          "Erreur consommation serveur, fallback localStorage",
          "quota",
          {
            error: error instanceof Error ? error.message : String(error),
          },
        );
      }
    }

    console.log(`[quotaTracking] 💾 Fallback localStorage...`);
    console.log(`[quotaTracking] 💾 Raison fallback:`, {
      isGuest,
      isE2E,
      hasUserId: !!userId,
      hasSession: false,
    });

    // Fallback localStorage (pour guests, E2E, ou si serveur indisponible)
    const storageData = localStorage.getItem(STORAGE_KEY);
    const allData: AllQuotaData = storageData ? JSON.parse(storageData) : {};

    // En fallback, utiliser directement localStorage sans appeler getQuotaConsumed
    // (qui pourrait bloquer avec getSession()). On utilisera les données existantes
    // ou créer des données par défaut.
    let userData = allData[currentUserId];

    if (!userData) {
      // Créer des données par défaut sans appeler getQuotaConsumed (qui pourrait bloquer)
      console.log(
        `[quotaTracking] 💾 Création données par défaut (pas d'appel getQuotaConsumed)`,
      );
      userData = {
        conversationsCreated: 0,
        datePollsCreated: 0,
        formPollsCreated: 0,
        quizzCreated: 0,
        availabilityPollsCreated: 0,
        aiMessages: 0,
        analyticsQueries: 0,
        simulations: 0,
        totalCreditsConsumed: 0,
        userId: currentUserId,
      };
    } else {
      // Migration automatique : ajouter les nouveaux compteurs s'ils n'existent pas
      if (userData.datePollsCreated === undefined)
        userData.datePollsCreated = 0;
      if (userData.formPollsCreated === undefined)
        userData.formPollsCreated = 0;
      if (userData.quizzCreated === undefined) userData.quizzCreated = 0;
      if (userData.availabilityPollsCreated === undefined)
        userData.availabilityPollsCreated = 0;
    }

    // Mettre à jour les compteurs selon le type d'action
    switch (action) {
      case "conversation_created":
        userData.conversationsCreated =
          (userData.conversationsCreated || 0) + credits;
        break;
      case "poll_created": {
        // Incrémenter le compteur spécifique selon pollType (pollsCreated supprimé)
        const pollType = metadata?.pollType as
          | "date"
          | "form"
          | "quizz"
          | "availability"
          | undefined;
        if (pollType) {
          switch (pollType) {
            case "date":
              userData.datePollsCreated =
                (userData.datePollsCreated || 0) + credits;
              break;
            case "form":
              userData.formPollsCreated =
                (userData.formPollsCreated || 0) + credits;
              break;
            case "quizz":
              userData.quizzCreated = (userData.quizzCreated || 0) + credits;
              break;
            case "availability":
              userData.availabilityPollsCreated =
                (userData.availabilityPollsCreated || 0) + credits;
              break;
          }
        } else {
          // Si pollType n'est pas fourni en fallback, logger un warning
          logger.warn(
            "poll_created action without pollType in metadata (localStorage fallback)",
            "quota",
            {
              metadata,
              userId: currentUserId,
            },
          );
        }
        break;
      }
      case "ai_message":
        userData.aiMessages = (userData.aiMessages || 0) + credits;
        break;
      case "analytics_query":
        userData.analyticsQueries = (userData.analyticsQueries || 0) + credits;
        break;
      case "simulation":
        userData.simulations = (userData.simulations || 0) + credits;
        break;
    }

    // Mettre à jour le total
    userData.totalCreditsConsumed =
      (userData.totalCreditsConsumed || 0) + credits;
    userData.userId = currentUserId;

    // Mettre à jour la date d'abonnement si nécessaire
    // En fallback localStorage, éviter les appels bloquants - on utilisera la date existante ou null
    if (userId && userId !== "guest" && !userData.subscriptionStartDate) {
      // Ne pas appeler getSubscriptionStartDate en fallback car cela pourrait bloquer
      // La date sera mise à jour lors du prochain appel réussi au serveur
      console.log(
        `[quotaTracking] 💾 Skip getSubscriptionStartDate en fallback (évite blocage)`,
      );
    }

    allData[currentUserId] = userData;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(allData));

    // Ajouter au journal
    addJournalEntry({
      action,
      credits,
      userId: currentUserId,
      metadata,
    });

    logger.debug("Crédits consommés (localStorage)", "quota", {
      action,
      credits,
      total: userData.totalCreditsConsumed,
      userId: currentUserId,
    });
  } catch (error) {
    logger.error("Erreur lors de la consommation de crédits", error);
    throw error; // ✅ PROPAGER l'erreur pour que le try-catch de useMessageSender la capte
  }
}

/**
 * Incrémenter le compteur de conversations créées
 * BLOQUANT : Throw une erreur si la limite est atteinte
 */
export async function incrementConversationCreated(
  userId: string | null | undefined,
  conversationId?: string,
): Promise<void> {
  await consumeCredits(userId, 1, "conversation_created", { conversationId });
}

/**
 * Incrémenter le compteur de polls créés
 * BLOQUANT : Throw une erreur si la limite est atteinte
 * @param userId - ID de l'utilisateur (null pour guests)
 * @param pollId - ID du poll créé (optionnel)
 * @param pollType - Type du poll (OBLIGATOIRE: "date", "form", "quizz", ou "availability")
 */
export async function incrementPollCreated(
  userId: string | null | undefined,
  pollId: string | undefined,
  pollType: "date" | "form" | "quizz" | "availability",
): Promise<void> {
  // Validation stricte : pollType est obligatoire
  if (
    !pollType ||
    !["date", "form", "quizz", "availability"].includes(pollType)
  ) {
    const error = ErrorFactory.validation(
      `pollType is required and must be one of: "date", "form", "quizz", "availability". Received: ${pollType}`,
      `Type de poll requis pour la consommation de quota`,
      { pollType, pollId, userId },
    );
    logger.error(
      "incrementPollCreated called without valid pollType",
      "quota",
      {
        pollType,
        pollId,
        userId,
      },
    );
    throw error;
  }

  // Normaliser userId : si c'est un deviceId (commence par "dev-"), c'est un invité
  // On le convertit en null pour que consumeCredits le traite comme "guest"
  let normalizedUserId: string | null | undefined = userId;
  if (userId && userId.startsWith("dev-")) {
    normalizedUserId = null; // deviceId = invité = null pour le tracking
  }
  await consumeCredits(normalizedUserId, 1, "poll_created", {
    pollId,
    pollType,
  });
}

/**
 * Consommer des crédits pour un message IA
 * BLOQUANT : Throw une erreur si la limite est atteinte
 */
export async function consumeAiMessageCredits(
  userId: string | null | undefined,
  conversationId?: string,
): Promise<void> {
  await consumeCredits(userId, 1, "ai_message", { conversationId });
}

/**
 * Consommer des crédits pour une query analytics
 */
export function consumeAnalyticsCredits(
  userId: string | null | undefined,
  pollId?: string,
  query?: string,
): void {
  // Fire and forget - ne pas bloquer l'exécution
  consumeCredits(userId, 1, "analytics_query", {
    pollId,
    analyticsQuery: query,
  }).catch((error) => {
    logger.error("Erreur lors de la consommation analytics", error);
  });
}

/**
 * Consommer des crédits pour une simulation (5 crédits selon la doc)
 */
export function consumeSimulationCredits(
  userId: string | null | undefined,
  pollId?: string,
  simulationId?: string,
): void {
  // Fire and forget - ne pas bloquer l'exécution
  consumeCredits(userId, 5, "simulation", { pollId, simulationId }).catch(
    (error) => {
      logger.error("Erreur lors de la consommation simulation", error);
    },
  );
}

/**
 * Consommer des crédits pour une action personnalisée
 */
export function consumeCustomCredits(
  userId: string | null | undefined,
  credits: number,
  action: CreditActionType,
  metadata?: CreditJournalEntry["metadata"],
): void {
  // Fire and forget - ne pas bloquer l'exécution
  consumeCredits(userId, credits, action, metadata).catch((error) => {
    logger.error("Erreur lors de la consommation crédits personnalisés", error);
  });
}

/**
 * Réinitialiser les quotas consommés (pour tests ou admin)
 */
export function resetQuotaConsumed(userId: string | null | undefined): void {
  try {
    const storageData = localStorage.getItem(STORAGE_KEY);
    const allData: AllQuotaData = storageData ? JSON.parse(storageData) : {};
    const currentUserId = userId || getCurrentUserId();

    delete allData[currentUserId];
    localStorage.setItem(STORAGE_KEY, JSON.stringify(allData));

    logger.info("Quota consommé réinitialisé", "quota", {
      userId: currentUserId,
    });
  } catch (error) {
    logger.error("Erreur lors de la réinitialisation du quota", error);
  }
}
